import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { LumiereSection } from "@/mocks/sections";

interface BlindsLayerProps {
  section: LumiereSection;
  index: number;
  progress: number; // 0..1 reveal progress for this layer
  isFirst: boolean;
  isMobile: boolean;
  onNavigate?: (id: string) => void;
}

const STRIP_COUNT = 14;
const STRIP_DELAY = 45; // ms
const STRIP_DURATION = 600; // ms
const TOTAL_MS = STRIP_DURATION + (STRIP_COUNT - 1) * STRIP_DELAY;

// easeInOutCubic
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const clamp = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

export default function BlindsLayer({ section, index, progress, isFirst, isMobile, onNavigate }: BlindsLayerProps) {
  const routeNavigate = useNavigate();
  // For the first layer there's no transition — always fully visible
  const effectiveProgress = isFirst ? 1 : progress;

  // Compute strip clip-paths
  const strips = useMemo(() => {
    return Array.from({ length: STRIP_COUNT }).map((_, k) => {
      const startNorm = (k * STRIP_DELAY) / TOTAL_MS;
      const endNorm = (k * STRIP_DELAY + STRIP_DURATION) / TOTAL_MS;
      const raw = clamp((effectiveProgress - startNorm) / (endNorm - startNorm));
      const sp = ease(raw);

      const bandSize = 100 / STRIP_COUNT; // % of viewport height
      const stripTopBase = k * bandSize;
      const stripBottomBase = (STRIP_COUNT - k - 1) * bandSize;
      // Animate top inset from (stripTopBase + bandSize) down to stripTopBase
      const insetTop = stripTopBase + (1 - sp) * bandSize;

      return {
        clipPath: `inset(${insetTop}% 0 ${stripBottomBase}% 0)`,
      };
    });
  }, [effectiveProgress]);

  // Text reveals after ~70% of strips revealed
  const textActive = isFirst ? true : effectiveProgress > 0.7;

  // Mobile crossfade
  const mobileOpacity = isFirst ? 1 : effectiveProgress;

  return (
    <div
      className="absolute inset-0 w-screen h-screen pointer-events-none"
      style={{ zIndex: index + 1 }}
      aria-hidden={effectiveProgress < 0.5}
    >
      {/* Image layer */}
      {isMobile ? (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${section.image})`,
            opacity: mobileOpacity,
            transition: "opacity 600ms ease-out",
          }}
        />
      ) : (
        <>
          {strips.map((s, k) => (
            <div
              key={k}
              className="absolute inset-0 bg-cover bg-center will-change-[clip-path]"
              style={{
                backgroundImage: `url(${section.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                clipPath: s.clipPath,
                WebkitClipPath: s.clipPath,
              }}
            />
          ))}
        </>
      )}

      {/* White / soft-blue vignette for readability */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.70) 35%, rgba(255,255,255,0.15) 65%, rgba(230,240,255,0.35) 100%)",
          opacity: isFirst ? 1 : effectiveProgress,
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.40) 0%, rgba(255,255,255,0) 25%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.42) 100%)",
          opacity: isFirst ? 1 : effectiveProgress,
        }}
      />

      {/* Text content */}
      <div className="absolute inset-0 flex items-center pointer-events-none">
        <div
          className={`px-6 md:px-16 lg:px-24 max-w-[1180px] lum-text-reveal ${
            textActive ? "is-active" : ""
          }`}
        >
          <div className="font-label text-[11px] md:text-[12px] text-[var(--lum-gold)] mb-6 md:mb-8">
            {section.label}
          </div>
          <h2 className="font-jp text-5xl md:text-7xl lg:text-[84px] leading-[1.15] text-[var(--lum-cream)] mb-4 md:mb-5 whitespace-pre-line font-medium">
            {section.headline}
          </h2>
          {section.subline && (
            <p className="font-jp text-lg md:text-2xl text-[var(--lum-gold)] mb-5 md:mb-6 font-normal whitespace-pre-line">
              {section.subline}
            </p>
          )}
          <p className="font-jp text-sm md:text-base lg:text-[16px] leading-[1.9] text-[var(--lum-cream)]/75 mb-8 md:mb-10 max-w-[460px]">
            {section.body}
          </p>
          {section.cta && (
            <div className="flex flex-col items-start gap-4 pointer-events-auto">
              <a
                href={section.cta.href}
                onClick={(e) => {
                  e.preventDefault();
                  const href = section.cta!.href;
                  if (href.startsWith("#")) {
                    const id = href.replace("#", "");
                    const el = document.getElementById(id);
                    if (el) {
                      const top = el.getBoundingClientRect().top + window.scrollY - 80;
                      window.scrollTo({ top, behavior: "smooth" });
                    }
                  } else if (href.startsWith("/")) {
                    routeNavigate(href);
                  } else {
                    onNavigate?.(href.replace("#", ""));
                  }
                }}
                className="group inline-flex items-center gap-3 px-7 py-4 bg-[var(--lum-gold)] text-white font-jp text-[14px] font-medium hover:bg-[#1d4ed8] transition-all duration-500 cursor-pointer whitespace-nowrap rounded-full hover:-translate-y-0.5"
              >
                <span>{section.cta.text}</span>
                <i className="ri-arrow-right-line text-base transition-transform duration-500 group-hover:translate-x-1"></i>
              </a>
              {section.subLink && (
                <span className="font-jp text-xs md:text-sm text-[var(--lum-cream)]/65">
                  {section.subLink}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}